import { getProductsStationSuggestions } from '@/api/generated/products-stations/products-stations';
import type { ListRequest, StationSuggestion } from '@/api/generated/schemas';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  selectorPresets,
  toListRequest,
  type NoPickFilters,
} from '@/shared/ui/async-selector';
import type { StationRef } from '../productsApi';

/**
 * Selektor stanic ze sdíleného číselníku — používá se v agendě Trasy pro přidávání zastávek
 * v režimu tlačítka (`trigger="button"` + `addMode`, viz async-selector README §2). Proto se
 * exportuje typový <c>.Selector</c> (ne `…Field`) a renderuje přímo s vlastním `onChange`.
 */
const stationSelector = defineEntityAsyncSelector<
  StationRef,
  StationSuggestion,
  ListRequest,
  NoPickFilters
>({
  api: {
    fetch: getProductsStationSuggestions,
    buildApiParams: (ctx) => toListRequest(ctx.search, ctx.ids),
    queryKey: (request) => listRequestQueryKey('products.stations.suggestions', request),
    mapItem: (s) => ({
      stationId: s.id,
      name: s.name ?? '',
      code: s.code ?? null,
      isDomestic: s.isDomestic,
      latitude: s.latitude ?? null,
      longitude: s.longitude ?? null,
    }),
  },
  key: (item) => item.stationId,
  objectCategory: 'Station',
  // Tři tečky (⋯) otevřou list page ObjectCategory v Magic desktopu (jako ostatní selektory).
  listPicker: 'legacy',
  display: {
    kind: 'nameCode',
    name: (item) => item.name,
    code: (item) => item.code,
  },
  columns: (t) =>
    selectorPresets.nameAndCode({
      name: {
        id: 'name',
        header: t('stationChainSelectors.station.columns.name'),
        value: (item) => item.name,
        sortable: true,
      },
      code: {
        id: 'code',
        header: t('stationChainSelectors.station.columns.code'),
        value: (item) => item.code,
        emptyDash: '—',
      },
    }),
  i18n: { namespace: 'products' },
});

export const StationSelector = stationSelector.Selector;
